/**
 * Price-range measurement renderer (TradingView "Price Range"). A two-anchor
 * rectangle (A = start, B = end) with a centred vertical double-arrow and a
 * label showing the price change, pips, %, and the bars/time it spans between
 * the anchors. Direction-coloured (green up / red down). Mirrors the fib
 * renderer's ctx style (interactive anchor handles + per-drawing menu);
 * non-data, SVG-only.
 */
import type { MouseEvent, MutableRefObject, RefObject, ReactNode } from "react";
import type { IChartApi } from "lightweight-charts";
import {
  type PriceRangeBox,
  type SelectedDrawing,
  type LineStyleName,
  setSelectedDrawing,
} from "@/state/slices/uiSlice";
import type { AppDispatch } from "@/state/store";
import { startBodyDrag } from "@/tools/_shared/bodyDrag";
import { fmtCountdown } from "@/utils/chartTime";
import { svgDasharrayFromName } from "./priceLineStyles";
import type { AnySeries } from "./chartData";
import type { DragTarget, MenuItem } from "./types";
import styles from "./Chart.module.css";

export interface PriceRangeRenderCtx {
  seriesRef: MutableRefObject<AnySeries | null>;
  chartRef: MutableRefObject<IChartApi | null>;
  containerRef: RefObject<HTMLDivElement>;
  dragRef: MutableRefObject<DragTarget | null>;
  timeToX: (time: number) => number | null;
  selectedDrawing: SelectedDrawing | null;
  startDrag: (
    target: Exclude<DragTarget, { type: "trading" } | { type: "draft" }>,
  ) => (e: MouseEvent) => void;
  startLockedPan: (nativeEvent: globalThis.MouseEvent) => void;
  showMenu: (items: MenuItem[]) => (e: MouseEvent) => void;
  dispatch: AppDispatch;
  tabId: string;
  setIsDragging: (v: boolean) => void;
  priceRangeMenuItems: (id: string) => MenuItem[];
  digits: number;
  /** Price per 1 pip for the active symbol (symbolMeta.pipSizeFor). */
  pipSize: number;
  /** Seconds per bar at the current timeframe — for the bars count. */
  barSeconds: number;
  /** Configured box/arrow/border colour (default when unset). */
  boxColor?: string;
  /** Label colour: follows the box colour when true, else `labelColorPref`. */
  textMatch: boolean;
  /** Configured label colour (used when `textMatch` is false). */
  labelColorPref?: string;
  /** Which side the two edge price values render on (default "right"). */
  labelSide: "left" | "right";
  /** Box border line thickness in px. */
  boxWidth: number;
  /** Label font weight. */
  labelWeight: "normal" | "bold";
  /** Centre double-arrow: show/hide (position is per-box `arrowFraction`). */
  arrow: boolean;
  /** 50% midline (wave pullback level): show/hide + style + thickness. */
  midline: boolean;
  midlineStyle: LineStyleName;
  midlineWidth: number;
}

/** Default Price Range colour — a light green. Boxes use this unless the user
 *  picks a colour in settings. Not direction-dependent. */
export const PRICE_RANGE_DEFAULT_COLOR = "#4CAF50";

export function renderPriceRange(
  box: PriceRangeBox,
  isPreview: boolean,
  ctx: PriceRangeRenderCtx,
): ReactNode {
  if (box.visible === false) return null;
  const series = ctx.seriesRef.current;
  if (!series) return null;

  const startX = ctx.timeToX(box.startTime);
  const endX = ctx.timeToX(box.endTime);
  const startY = series.priceToCoordinate(box.startPrice);
  const endY = series.priceToCoordinate(box.endPrice);
  if (startX == null || endX == null || startY == null || endY == null)
    return null;

  const left = Math.min(startX, endX);
  const right = Math.max(startX, endX);
  const width = Math.max(2, right - left);
  const top = Math.min(startY, endY);
  const bottom = Math.max(startY, endY);
  const height = Math.max(1, bottom - top);

  const isSelected =
    !isPreview &&
    ctx.selectedDrawing?.kind === "priceRange" &&
    ctx.selectedDrawing.id === box.id;

  // Metrics: A (start) → B (end). Up when B is above A.
  const priceDelta = box.endPrice - box.startPrice; // signed — value + placement
  const up = priceDelta >= 0;
  // Single configurable colour (default light green) — no direction logic.
  const color = ctx.boxColor || PRICE_RANGE_DEFAULT_COLOR;
  const labelColor = ctx.textMatch ? color : ctx.labelColorPref || color;
  const fontWeight = ctx.labelWeight === "normal" ? 400 : 600;
  const boxStroke = ctx.boxWidth || 2;
  const pct = box.startPrice ? (priceDelta / box.startPrice) * 100 : 0;
  const pips = ctx.pipSize > 0 ? priceDelta / ctx.pipSize : 0;
  const span = Math.abs(box.endTime - box.startTime);
  const bars = ctx.barSeconds > 0 ? Math.round(span / ctx.barSeconds) : 0;
  const dur = span > 0 ? fmtCountdown(span) : "0s";

  const sign = priceDelta >= 0 ? "+" : "−";
  const absDelta = Math.abs(priceDelta).toFixed(ctx.digits);
  const absPips = Math.abs(pips);
  const line1 = `${sign}${absDelta} (${sign}${Math.abs(pct).toFixed(2)}%)`;
  const line2 = `${sign}${absPips >= 100 ? Math.round(absPips) : absPips.toFixed(1)} pips`;
  const line3 = `${bars} bar${bars === 1 ? "" : "s"} · ${dur}`;

  // Single-line label, OUTSIDE the box on the side the arrow points to (above
  // for up, below for down) so the candles inside stay visible. No background
  // — the direction colour (green up / red down) keeps it legible on both the
  // light and dark themes.
  const cx = (left + right) / 2;
  // Arrow x — slid horizontally via `arrowFraction` (0.5 = centred). Map the
  // fraction through the box's time span so it stays anchored to a time as the
  // chart pans/zooms; fall back to pixel interpolation if that time is off the
  // visible scale. Clamped inside the box edges.
  const arrowFrac = box.arrowFraction ?? 0.5;
  const arrowTime = box.startTime + arrowFrac * (box.endTime - box.startTime);
  const arrowXRaw = ctx.timeToX(arrowTime);
  const arrowX = Math.max(
    left,
    Math.min(right, arrowXRaw ?? left + arrowFrac * (right - left)),
  );
  // The whole-box move grab tracks the visible arrow, but snaps back to centre
  // when the arrow is hidden so the (invisible) grab stays where you'd reach for.
  const moveX = ctx.arrow ? arrowX : cx;
  const label = `${line1} · ${line2} · ${line3}`;
  const labelY = up ? Math.max(11, top - 6) : bottom + 15;
  // Price at each edge: the top line sits at the higher price, the bottom line
  // at the lower price (smaller Y = higher price).
  const topPrice = Math.max(box.startPrice, box.endPrice);
  const bottomPrice = Math.min(box.startPrice, box.endPrice);

  return (
    <g key={isPreview ? "preview-priceRange" : box.id} opacity={isPreview ? 0.6 : 1}>
      {/* Range fill — no side borders. The top/bottom edges are drawn as
          horizontal lines at the two price levels (TradingView style); the
          left/right sides are intentionally open. Visual only: `pointerEvents:
          none` so a click inside the box passes through to the chart (lets you
          draw a new drawing overlapping this box). The box is grabbed only at its
          two corner handles + the centre vertical. */}
      <rect
        x={left}
        y={top}
        width={width}
        height={height}
        fill={color}
        fillOpacity={0.1}
        pointerEvents="none"
      />
      <line
        x1={left}
        x2={right}
        y1={top}
        y2={top}
        stroke={color}
        strokeOpacity={isSelected ? 0.9 : 0.6}
        strokeWidth={isSelected ? boxStroke + 0.5 : boxStroke}
        pointerEvents="none"
      />
      <line
        x1={left}
        x2={right}
        y1={bottom}
        y2={bottom}
        stroke={color}
        strokeOpacity={isSelected ? 0.9 : 0.6}
        strokeWidth={isSelected ? boxStroke + 0.5 : boxStroke}
        pointerEvents="none"
      />

      {/* Price value above each edge line, on the configured side. Left =
          anchored to the box's left edge (text starts there); right = the box's
          right edge (text ends there). */}
      {(() => {
        const onLeft = ctx.labelSide === "left";
        const priceX = onLeft ? left : right;
        const anchor = onLeft ? "start" : "end";
        return (
          <>
            <text
              x={priceX}
              y={top - 4}
              textAnchor={anchor}
              fill={labelColor}
              fontSize={11}
              fontWeight={fontWeight}
              pointerEvents="none"
            >
              {topPrice.toFixed(ctx.digits)}
            </text>
            <text
              x={priceX}
              y={bottom - 4}
              textAnchor={anchor}
              fill={labelColor}
              fontSize={11}
              fontWeight={fontWeight}
              pointerEvents="none"
            >
              {bottomPrice.toFixed(ctx.digits)}
            </text>
          </>
        );
      })()}

      {/* Centre vertical double-arrow spanning the price move. Slid left/right
          via `arrowFraction`; hidden when the Arrow toggle is off. */}
      {ctx.arrow && (
        <>
          <line
            x1={arrowX}
            x2={arrowX}
            y1={top + 6}
            y2={bottom - 6}
            stroke={color}
            strokeWidth={1.5}
            pointerEvents="none"
          />
          <polygon
            points={`${arrowX},${top} ${arrowX - 4},${top + 7} ${arrowX + 4},${top + 7}`}
            fill={color}
            pointerEvents="none"
          />
          <polygon
            points={`${arrowX},${bottom} ${arrowX - 4},${bottom - 7} ${arrowX + 4},${bottom - 7}`}
            fill={color}
            pointerEvents="none"
          />
        </>
      )}

      {/* Pullback midline — the wave's retracement level. DRAGGABLE up/down to
          any %, positioned at `midlineFraction` of the range (0.5 = 50% by
          default). Label shows the live "<pct>% · <price>". Hidden unless
          enabled in settings. */}
      {ctx.midline &&
        (() => {
          const frac = box.midlineFraction ?? 0.5;
          const midPrice =
            box.startPrice + frac * (box.endPrice - box.startPrice);
          const midY = series.priceToCoordinate(midPrice);
          if (midY == null) return null;
          const onLeft = ctx.labelSide === "left";
          const lx = onLeft ? left : right;
          const anchor = onLeft ? "start" : "end";
          return (
            <>
              <line
                x1={left}
                x2={right}
                y1={midY}
                y2={midY}
                stroke={color}
                strokeOpacity={0.85}
                strokeWidth={ctx.midlineWidth}
                strokeDasharray={svgDasharrayFromName(ctx.midlineStyle)}
                pointerEvents="none"
              />
              <text
                x={lx}
                y={midY - 4}
                textAnchor={anchor}
                fill={labelColor}
                fontSize={11}
                fontWeight={fontWeight}
                pointerEvents="none"
              >
                {(frac * 100).toFixed(1)}% · {midPrice.toFixed(ctx.digits)}
              </text>
              {/* Wide transparent grab band — drag to move the % up/down. */}
              {!isPreview && (
                <line
                  x1={left}
                  x2={right}
                  y1={midY}
                  y2={midY}
                  stroke="transparent"
                  strokeWidth={10}
                  className={styles.posBodyHit}
                  data-drawing-hit="true"
                  style={{ cursor: box.locked ? undefined : "ns-resize" }}
                  onMouseDown={
                    box.locked
                      ? (e: MouseEvent) => {
                          if (e.button !== 0) return;
                          ctx.startLockedPan(e.nativeEvent);
                          ctx.dispatch(
                            setSelectedDrawing({
                              tabId: ctx.tabId,
                              selected: { kind: "priceRange", id: box.id },
                            }),
                          );
                        }
                      : ctx.startDrag({
                          type: "priceRange",
                          id: box.id,
                          anchor: "midline",
                          startPrice: box.startPrice,
                          endPrice: box.endPrice,
                        })
                  }
                  onContextMenu={ctx.showMenu(ctx.priceRangeMenuItems(box.id))}
                />
              )}
            </>
          );
        })()}

      {/* Single-line label — no background, direction-coloured (legible on
          light + dark), outside the box so candles inside stay visible. */}
      <text
        x={cx}
        y={labelY}
        textAnchor="middle"
        fill={labelColor}
        fontSize={11}
        fontWeight={fontWeight}
        pointerEvents="none"
      >
        {label}
      </text>

      {/* Anchor handles — drag the corner dots to reshape (skipped in preview).
          The centre vertical is the MOVE grab. */}
      {!isPreview && (
        <>
          {/* Centre-vertical grab — the move zone. Drag it to MOVE the whole
              box. Tracks `arrowX` so it stays under the visible arrow; remains
              grabbable (transparent) even when the arrow is hidden so the box is
              always movable. Snapshots both anchors + the mousedown point so the
              drag loop applies a clean delta to all four fields, and suppresses
              chart pan. Rendered before the grip + corner dots so those keep
              priority (slide the arrow / reshape the ends). */}
          <line
            x1={moveX}
            x2={moveX}
            y1={top}
            y2={bottom}
            stroke="transparent"
            strokeWidth={12}
            className={styles.posBodyHit}
            data-drawing-hit="true"
            onMouseDown={box.locked
              ? (e: MouseEvent) => { if (e.button !== 0) return; ctx.startLockedPan(e.nativeEvent); ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "priceRange", id: box.id } })); }
              : (e) => startBodyDrag(e, ctx, "priceRange", box)}
            onContextMenu={ctx.showMenu(ctx.priceRangeMenuItems(box.id))}
          />

          {/* Arrow slide grip — a small handle at the arrow's midpoint that
              SLIDES the arrow left/right (ew-resize). Sits on top of the move
              band so a press here repositions the arrow instead of moving the
              box; everywhere else on the vertical still moves the box. Only when
              the arrow is shown. */}
          {ctx.arrow &&
            (() => {
              const gy = (top + bottom) / 2;
              return (
                <>
                  <rect
                    x={arrowX - 5}
                    y={gy - 4}
                    width={10}
                    height={8}
                    rx={2}
                    fill={isSelected ? "#3b82f6" : "#161922"}
                    stroke={color}
                    strokeWidth={1.5}
                    pointerEvents="none"
                  />
                  <rect
                    x={arrowX - 9}
                    y={gy - 9}
                    width={18}
                    height={18}
                    fill="transparent"
                    className={styles.posBodyHit}
                    data-drawing-hit="true"
                    style={{ cursor: box.locked ? undefined : "ew-resize" }}
                    onMouseDown={
                      box.locked
                        ? (e: MouseEvent) => {
                            if (e.button !== 0) return;
                            ctx.startLockedPan(e.nativeEvent);
                            ctx.dispatch(
                              setSelectedDrawing({
                                tabId: ctx.tabId,
                                selected: { kind: "priceRange", id: box.id },
                              }),
                            );
                          }
                        : ctx.startDrag({
                            type: "priceRange",
                            id: box.id,
                            anchor: "arrow",
                            startTime: box.startTime,
                            endTime: box.endTime,
                          })
                    }
                    onContextMenu={ctx.showMenu(ctx.priceRangeMenuItems(box.id))}
                  />
                </>
              );
            })()}
          <circle
            cx={startX}
            cy={startY}
            r={isSelected ? 6 : 5}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={color}
            strokeWidth={1.5}
            className={styles.fibHandle}
            data-drawing-hit="true"
            onMouseDown={box.locked
              ? (e: MouseEvent) => { if (e.button !== 0) return; ctx.startLockedPan(e.nativeEvent); ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "priceRange", id: box.id } })); }
              : ctx.startDrag({ type: "priceRange", id: box.id, anchor: "start" })}
            onContextMenu={ctx.showMenu(ctx.priceRangeMenuItems(box.id))}
          />
          <circle
            cx={endX}
            cy={endY}
            r={isSelected ? 6 : 5}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={color}
            strokeWidth={1.5}
            className={styles.fibHandle}
            data-drawing-hit="true"
            onMouseDown={box.locked
              ? (e: MouseEvent) => { if (e.button !== 0) return; ctx.startLockedPan(e.nativeEvent); ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "priceRange", id: box.id } })); }
              : ctx.startDrag({ type: "priceRange", id: box.id, anchor: "end" })}
            onContextMenu={ctx.showMenu(ctx.priceRangeMenuItems(box.id))}
          />
        </>
      )}
    </g>
  );
}
